<script lang="ts">
	//UI
	import StoreMember from "./StoreMember.svelte";
	import EditIcon from "@hitit/hub/src/assets/Icons/Edit.png";
	import SaveIcon from "@hitit/hub/src/assets/Icons/Save.png";
	import ComponentLoading from "../common/ComponentLoading.svelte";
	import ComponentCover from "../common/ComponentCover.svelte";
	import { Card, PlusIcon } from "@hitit/new_ui";

	//Utils
	import { t, api } from "../../../stores";
	//Types
	import type { Member } from "./StoreMember.svelte";
	import type { AccessControlDTO } from "@hitit/api";
	//Props
	export let accessControlList: AccessControlDTO[] = [];
	export let editmode = false;
	export let editable = false;
	export let coverStatus = false;
	export let storeId = "";
	//Constants
	//Variables
	let memberList: Member[] = [];
	let loading = false;
	let error = "";
	//Functions
	async function actionHandler() {
		if (!editmode) {
			editmode = true;
			return;
		}
		memberList = memberList.map((v) => ({ ...v, error: "" }));
		const originStoreId = storeId;
		const noneUsernameList = memberList.filter((v) => v.username === "");
		const noneUsernameError = noneUsernameList.length >= 1;
		const sameUsernameError =
			new Set(memberList.map((v) => v.username)).size < memberList.length;
		const noneStoreOwnerError =
			memberList.filter((v) => v.permission === "STOREOWNER").length === 0;

		if (noneStoreOwnerError) error = $t("mbr.error.none_storeowner");
		if (sameUsernameError) {
			const nameCount: any = {};
			for (const member of memberList) {
				const value = (nameCount[member.username] || 0) + 1;
				nameCount[member.username] = value;
			}
			const moreThanTwo = Object.entries(nameCount)
				.filter(([name, count]) => (count as number) > 1)
				.map(([name]) => name as string);
			memberList
				.filter((v) => moreThanTwo.includes(v.username))
				.forEach((v) => (v.error = $t("mbr.error.same_username")));
		}

		for (const member of noneUsernameList)
			member.error = $t("mbr.error.none_username");

		if (noneUsernameError || sameUsernameError || noneStoreOwnerError) {
			memberList = memberList;
			return;
		}
		try {
			//
			error = "";
			loading = true;
			const postParams = memberList.map((v) => ({
				storeId: v.storeId,
				username: v.username,
				permission: v.permission,
			}));

			const postResponse = await $api.postAccessControl(storeId, {
				accessControlDTOList: postParams,
			});

			if (postResponse.data.status !== "OK") {
				const USERPROFILE_NOT_FOUND = "USERPROFILE_NOT_FOUND";
				if (postResponse.data.status === USERPROFILE_NOT_FOUND) {
					const userNotFounds =
						postResponse.data.accessControlListDTO.accessControlDTOList
							.filter((v) => v.status === USERPROFILE_NOT_FOUND)
							.map((v) => v.username);
					memberList = memberList.map((v) => {
						const error = userNotFounds.includes(v.username)
							? $t("mbr.error.account_does_not_exist")
							: "";
						return {
							...v,
							error,
						};
					});
				} else {
					throw new Error("unknown Error");
				}
				return;
			}

			const getResponse = await $api.getAccessControlByStoreId(storeId);
			if (originStoreId !== storeId) {
				return;
			}
			accessControlList =
				getResponse.data.accessControlListDTO.accessControlDTOList;
			editmode = false;
		} catch (e) {
			if (originStoreId !== storeId) {
				const message = `${originStoreId} : ${$t("sso.error.unknownError")}`;
				alert(message);
				return;
			}
			error = $t("sso.error.unknownError");
		} finally {
			loading = false;
		}
	}
	function deleteMemberHandler(e: { detail: Member }) {
		memberList = memberList.filter((v) => v !== e.detail);
	}

	function addButtonHandler() {
		const newMember: Member = {
			storeId,
			userId: "",
			username: "",
			permission: "USER",
			error: "",
		};
		memberList.push(newMember);
		memberList = memberList;
	}

	function convertMemberList(accessControlDTOList: AccessControlDTO[]) {
		memberList = accessControlDTOList.map((v) => ({
			permission: v.permission,
			username: v.username,
			userId: v.userId,
			storeId: storeId,
			cellphone: v.cellphone,
			cellphoneCountryCode: v.cellphoneCountryCode,
			error: "",
			name: v.name || "",
		}));
	}
	//Do
	$: ((accessControlDTOList: AccessControlDTO[]) => {
		convertMemberList(accessControlDTOList);
	})(accessControlList);

</script>

<Card noPadding>
	<div class="container" t-id="orgarnization">
		{#if coverStatus}
			<ComponentCover />
		{/if}
		{#if loading}
			<ComponentLoading coverMode />
		{/if}
		{#if editable}
			<div class="action" on:click={actionHandler}>
				<span>{editmode ? $t('global.done') : $t('global.edit')}</span>
				<img
					src={editmode ? SaveIcon : EditIcon}
					alt={editmode ? 'save' : 'edit'} />
			</div>
		{/if}
		<p class="title">{$t('hub.store.storeinfo.Organization_Workers')}</p>
		{#if editmode && error}
			<p class="error">{error}</p>
		{/if}
		<div class="members">
			{#each memberList as member}
				<div class="member">
					<StoreMember
						bind:member
						editmode={editmode}
						on:DELETE_MEMBER={deleteMemberHandler} />
				</div>
			{/each}
			{#if editmode}
				<button class="member add" on:click={addButtonHandler}>
					<span class="icon">
						<PlusIcon />
					</span>
				</button>
			{/if}
			<div class="member dummy" />
			<div class="member dummy" />
		</div>
	</div>
</Card>

<style lang="scss">
	@import "../../../variables.scss";
	.container {
		width: 100%;
		display: flex;
		flex-direction: column;
		padding: 20px 10px;
		gap: 10px;

		.error {
			color: #e54848;
		}
		.title {
			font-size: 1.25rem;
			font-weight: 600;
			color: #46474b;
		}
		.members {
			width: 100%;
			display: flex;
			flex-direction: column;
			gap: 10px;
			.member {
				width: 100%;

				&.dummy {
					display: none;
				}

				&.add {
					display: flex;
					align-items: center;
					justify-content: center;
					height: 105px;
					box-sizing: border-box;
					border: 1px solid #bcbfc3;
					background-color: transparent;
					border-radius: 5px;
					.icon {
						width: 35px;
						height: 35px;
					}

					&:hover {
						cursor: pointer;
					}
				}
			}
		}

		.action {
			display: flex;
			gap: 10px;
			align-items: center;
			position: absolute;
			right: 10px;
			top: 10px;
			color: #6d6f76;
			font-weight: 500;

			&:hover {
				cursor: pointer;
			}
			img {
				width: 24px;
				height: 24px;
				object-fit: contain;
			}
		}
	}
	@include DesktopSize {
		.container {
			padding: 30px;
			gap: 30px;
			.action {
				top: 20px;
				right: 20px;
				font-size: 1.25rem;
			}
			.title {
				font-size: 2.125rem;
			}

			.members {
				gap: 20px;

				.member {
					flex-basis: 30%;
					flex-grow: 1;
					min-width: 350px;

					&.dummy {
						display: block;
					}
					&.add {
						height: 114px;
					}
				}
			}
		}
	}

</style>
